import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { getWithParams, del } from '../../utils/fetchAPI';
import { FaEdit, FaTrash, FaPlus, FaUserMd } from 'react-icons/fa';
import PageTitle from "../../components/page-title/pageTitle";
import { AnimatePresence, motion } from "framer-motion";
import Loader from "../../components/loader/loder";
import CustomPagination from "../../components/pagination/CustomPagination";
import { swalDel } from "../../utils/SwalUti";
import { toast } from "react-toastify";
import SpecialityForm from "./speciality-form";

const SpecialityList = () => {
    const [specialities, setSpecialities] = useState([]);
    const [selectedSpeciality, setSelectedSpeciality] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [totalRows, setTotalRows] = useState(0);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState('');

    useEffect(() => {
        fetchSpecialities();
    }, [currentPage, perPage, search, activeFilter]);

    const fetchSpecialities = async () => {
        setIsLoading(true);
        try {
            const params = {
                per_page: perPage,
                current_page: currentPage,
            };

            if (search) {
                params.search = search;
            }

            if (activeFilter) {
                params.active = activeFilter;
            }

            const response = await getWithParams('/speciality/list', params);

            if (response?.status === 200) {
                setSpecialities(response.data?.specialities || []);
                setTotalRows(response.data?.total || 0);
            }
        } catch (error) {
            console.error('Error fetching specialities:', error);
            toast.error('Failed to fetch specialities', {
                position: "top-center",
                theme: "light",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (speciality) => {
        setSelectedSpeciality(speciality);
        setShowPopup(true);
    };

    const handleDelete = async (speciality) => {
        const result = await swalDel(`Are you sure you want to delete "${speciality.name}"? It will no longer be offered when tagging a doctor.`);

        if (result.isConfirmed) {
            try {
                await del(`/speciality/${speciality._id}`);
                toast.success('Speciality deleted successfully!', {
                    position: "top-center",
                    theme: "light",
                });
                fetchSpecialities();
            } catch (error) {
                console.error('Error deleting speciality:', error);
                toast.error('Failed to delete speciality. Please try again.', {
                    position: "top-center",
                    theme: "light",
                });
            }
        }
    };

    const handleClosePopup = () => {
        setShowPopup(false);
        setSelectedSpeciality(null);
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1); // Reset to first page on search
    };

    const handleActiveFilterChange = (e) => {
        setActiveFilter(e.target.value);
        setCurrentPage(1); // Reset to first page on filter change
    };

    const columns = [
        {
            name: '#',
            selector: (row, index) => (currentPage - 1) * perPage + index + 1,
            width: '70px',
            center: true,
        },
        {
            name: 'Name',
            selector: row => row?.name,
            sortable: true,
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Code',
            selector: row => row?.code,
            sortable: true,
            wrap: true,
            minWidth: '180px',
            cell: (row) => (
                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                    {row?.code}
                </span>
            ),
        },
        {
            name: 'Description',
            selector: row => row?.description || '-',
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Status',
            selector: row => row?.active ? 'Active' : 'Inactive',
            sortable: true,
            width: '130px',
            cell: (row) => (
                <span className={`px-2 py-1 rounded text-sm ${row?.active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-gray-800'
                    }`}>
                    {row?.active ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            name: 'Order',
            selector: row => row?.order ?? 0,
            sortable: true,
            width: '100px',
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="flex gap-2">
                    <button
                        className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={() => handleEdit(row)}
                        title="Edit"
                    >
                        <FaEdit />
                    </button>
                    <button
                        className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={() => handleDelete(row)}
                        title="Delete"
                    >
                        <FaTrash />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#50b6b5',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 'bold',
            },
        },
        rows: {
            style: {
                fontSize: '0.9rem',
            },
            stripedStyle: {
                backgroundColor: '#f6f6f6',
            },
            highlightOnHoverStyle: {
                backgroundColor: '#e0e0e0',
            },
        },
    };

    return (
        <div className="flex justify-center flex-col gap-5 w-full">
            <div className="flex justify-between items-center">
                <PageTitle title="Doctor Speciality" icon={<FaUserMd />} />
                <button
                    onClick={() => {
                        setSelectedSpeciality(null);
                        setShowPopup(true);
                    }}
                    className="flex items-center justify-center gap-2 text-sm px-4 py-2 bg-primary text-white rounded-md hover:bg-teal-700"
                >
                    <FaPlus className="w-4 h-4" />
                    Create New Speciality
                </button>
            </div>

            {/* Search and Filter Section */}
            <div className="bg-white rounded-md p-4 shadow-md">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <input
                            type="text"
                            value={search}
                            onChange={handleSearch}
                            placeholder="Search by name or code..."
                            className="w-full text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800"
                        />
                    </div>
                    <div className="w-full md:w-48">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Active Status</label>
                        <select
                            value={activeFilter}
                            onChange={handleActiveFilterChange}
                            className="w-full text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800"
                        >
                            <option value="">All</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="text-center text-gray-500"><Loader /></div>
            ) : (
                <DataTable
                    columns={columns}
                    data={specialities}
                    customStyles={customStyles}
                    highlightOnHover
                    striped
                    noDataComponent="No specialities found"
                />
            )}

            <CustomPagination
                currentPage={currentPage}
                totalRows={totalRows}
                perPage={perPage}
                setCurrentPage={setCurrentPage}
                setPerPage={setPerPage}
            />

            <AnimatePresence>
                {showPopup && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            className="bg-white p-5 rounded-md shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                        >
                            <SpecialityForm
                                speciality={selectedSpeciality}
                                onSuccess={fetchSpecialities}
                                onClose={handleClosePopup}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SpecialityList;
